/**
 * Estado documental de UN recurso (EPP, vehículo o maquinaria), plegado desde
 * el conteo que calcula el servidor (`ResumenDocumentosRecursoService`).
 *
 * Es la ÚNICA función que interpreta esos números: la columna Documentos la usa
 * para pintar cada fila y las tarjetas de KPI la usan para contar cuántos
 * recursos caen en cada caso. Si el criterio de "al día" se escribiera dos
 * veces —una por pantalla, o una por columna y otra por tarjeta— la tarjeta y
 * la tabla terminarían diciendo cosas distintas.
 *
 * Vive en `lib/` y no dentro de una página porque las tres pantallas de equipos
 * comparten el mismo diseño y el mismo contrato de API.
 */

/**
 * Conteo de documentos de UN recurso. Sólo viene en los listados
 * (`GET /vehiculos`, `GET /maquinarias`): la ficha lee los documentos por su
 * propio endpoint.
 *
 * El denominador NO está acá a propósito — es el mismo número para todas las
 * filas y viaja una sola vez, en la clave `documentos` de la respuesta.
 */
export interface RecursoDocumentosResumen {
  /** Tipos del catálogo que ya tienen archivo subido. */
  cargados: number
  /** De los cargados, cuántos tienen `fecha_vencimiento` en el pasado. */
  vencidos: number
  /** De los cargados, cuántos entran en la ventana de aviso del servidor. */
  por_vencer: number
}

export interface EstadoDocumental {
  cargados: number
  faltantes: number
  vencidos: number
  porVencer: number
  /** Todo el catálogo cargado y nada vencido — lo que cuenta "Documentación completa". */
  completo: boolean
  /** Texto del badge: "al día", o el detalle de lo que falta. */
  nota: string
  tono: "success" | "warning" | "destructive"
}

function plural(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`
}

export function estadoDocumental(
  resumen: RecursoDocumentosResumen | undefined,
  tiposTotal: number,
): EstadoDocumental | null {
  // Sin catálogo (o sin resumen del servidor) no hay denominador, y cualquier
  // fracción sería inventada — la columna muestra un guion.
  if (!resumen || tiposTotal === 0) return null

  const { cargados, vencidos, por_vencer: porVencer } = resumen
  const faltantes = Math.max(0, tiposTotal - cargados)

  const partes: string[] = []
  if (faltantes > 0) partes.push(`${faltantes} sin subir`)
  if (vencidos > 0) partes.push(plural(vencidos, "vencido", "vencidos"))
  if (porVencer > 0) partes.push(`${porVencer} por vencer`)

  return {
    cargados,
    faltantes,
    vencidos,
    porVencer,
    completo: faltantes === 0 && vencidos === 0,
    nota: partes.length === 0 ? "al día" : partes.join(" · "),
    // Rojo si falta algo o ya venció; ámbar sólo cuando lo único pendiente es
    // una fecha que se acerca. El orden importa: un recurso con un vencido y
    // otro por vencer es un problema rojo, no ámbar.
    tono: faltantes > 0 || vencidos > 0 ? "destructive" : porVencer > 0 ? "warning" : "success",
  }
}
